/* eslint-disable @typescript-eslint/no-explicit-any */
import { Grid, LinearProgress, Paper, Typography } from '@material-ui/core'
import moment from 'moment-timezone'
import { FC, useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router'
import { Dispatch } from 'redux'
import { createApplication, getApartmentLead } from '../../actions/applications'
import ChangeLanguageForm from '../../components/ChangeLanguageForm'
import {
  selectCreateApplication,
  selectLeadApplication
} from '../../selectors/applications'
import i18n from '../../translations/i18n'
import { Apartment } from '../../types/apartments'
import { Application, PartnerLead } from '../../types/applications'
import { StateInterface } from '../../types/states'
import ApartmentsService from '../../services/apartments'
// import { bindSubmitActionToPromise } from '../../utils/forms'
import NewApplicationForm, { isOneAdult } from './NewApplicationForm'
import MinimalNewApplicationForm from './MinimalNewApplicationForm'
import NewApplicationProgress from './Progress'
import ApartmentSummaryCard from './ApartmentSummaryCard'

const App: FC = (props: any) => {
  const history = useHistory()

  const routeMatch = useRouteMatch()
  const apartmentCode = (routeMatch.params as any).apartmentCode
  const step = Number.parseInt((routeMatch.params as any).step || 0)
  const {
    location,
    getLeadData,
    application,
    lead,
    createApplication: submitApplication
  } = props
  const currentLanguage = i18n.language
  const initialValue = {
    firstName: '',
    lastName: '',
    email: '',
    phone: { phone: '358', countryDialCode: '358' },
    availabilities: [{ startDate: moment() }],
    notes: '',
    contactInformationConsent: true,
    marketingConsent: false
  }
  const [value, setValue] = useState(initialValue)
  const [applicationId, setApplicationId] = useState('')
  const [apartment, setApartment] = useState<Apartment | null>(null)
  const [listing, setListing] = useState<any>(null)
  const [isApartmentLoading, setIsApartmentLoading] = useState(true)

  const isMinimalAnnouncement =
    (apartment as any)?.announcementType === 'minimal'

  const navigateTo = useCallback(
    (newStep) => {
      history.push(`/new-application/${apartmentCode}/steps/${newStep}`)
    },
    [history, apartmentCode]
  )

  useEffect(() => {
    let mounted = true
    const fetchApartment = async () => {
      try {
        setIsApartmentLoading(true)
        const fetchedApartment = await ApartmentsService.getApartmentByCode(
          apartmentCode
        )

        if (mounted) {
          setApartment(fetchedApartment)
        }
        if (fetchedApartment?.status === 'marketing') {
          // Photos, size and availability for the summary card; optional, so failures are ignored
          ApartmentsService.getApartmentListingData(apartmentCode)
            .then((data) => {
              if (mounted) setListing(data)
            })
            .catch((): void => undefined)
        }
      } catch (error) {
        if (mounted) {
          setApartment(null)
        }
      } finally {
        if (mounted) {
          setIsApartmentLoading(false)
        }
      }
    }
    fetchApartment()
    const qs = new URLSearchParams(location.search)
    if (qs && qs.get('source')) {
      getLeadData(qs.get('source'))
    }
    return () => {
      mounted = false
    }
  }, [apartmentCode, location.search, getLeadData])

  useEffect(() => {
    if (isMinimalAnnouncement && step !== 0) {
      navigateTo(0)
    }
  }, [isMinimalAnnouncement, step, navigateTo])

  useEffect(() => {
    if (lead && lead.leadData && lead.leadData.metadata) {
      const leadData = lead.leadData.metadata
      const updatedValue = {
        ...initialValue,
        firstName: leadData.name.split(' ')[0] || '',
        lastName: leadData.name.split(' ')[1] || '',
        email: leadData.email || '',
        notes: leadData.notes || '',
        agreed:
          leadData.agreed === true || leadData.agreed === 'true' || false,
        contactInformationConsent:
          leadData.contactInformationConsent === true ||
          leadData.contactInformationConsent === 'true' ||
          false
      }
      if (leadData.phone && leadData.phone.trim()) {
        updatedValue.phone = { countryDialCode: '358', phone: leadData.phone }
      }
      setValue(updatedValue)
      setApplicationId(lead.leadData.applicationId)
    }
  }, [lead])

  const handleMinimalSubmit = useCallback(
    (formValues: any) => {
      const payload = {
        ...formValues,
        notes:
          formValues.notes ||
          'Olen kiinnostunut kohteestanne. Ottaisitko yhteyttä?',
        name: `${formValues.firstName || ''} ${formValues.lastName || ''}`.trim(),
        apartmentCode,
        language: currentLanguage,
        applicationId,
        contactInformationConsent:
          formValues.contactInformationConsent || false
      }
      submitApplication(payload)
    },
    [
      applicationId,
      submitApplication,
      apartmentCode,
      currentLanguage
    ]
  )

  if (isApartmentLoading) {
    return <LinearProgress />
  }

  if (!apartment) {
    return <LinearProgress />
  }

  return (
    <>
      {apartment.address ? (
        <>
          {
            apartment.status === 'rented' ? (
              <Grid container justify="center">
                <Grid item xs={10} sm={8} md={6} lg={4}>
                  <Paper style={{ margin: '50px 0', padding: '30px' }}>
                    <Typography variant='h5' gutterBottom style={{ textAlign: 'center' }}>
                      {i18n.t('newApplication.notAvailable')}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <>
                <div style={{ maxWidth: '500px', margin: '0 auto', padding: '8px 20px' }}>
                  <ApartmentSummaryCard apartment={apartment} listing={listing} />
                  <NewApplicationProgress
                    progress={
                      isMinimalAnnouncement
                        ? 100
                        : step === 0
                          ? 33
                          : ((step + 1) / 4) * 100
                    }
                  />
                </div>

                <ChangeLanguageForm
                  go={(arg: any) => {
                    navigateTo(step);
                  }}
                  isApplication={true}
                  style={{
                    right: window.innerWidth < 300 ? '3px' : window.innerWidth < 600 ? '5px' : '15px',
                    transform: 'none',
                    marginLeft: 'auto',
                    position: 'absolute',
                  }}
                />
                <Grid container justify='center'>
                  {!application &&
                    (isMinimalAnnouncement ? (
                      <MinimalNewApplicationForm
                        onSubmit={handleMinimalSubmit}
                        initialValues={{ ...value }}
                      />
                    ) : (
                      <NewApplicationForm
                        onSubmit={(_values: any) => {
                          if (
                            step === 0 ||
                            step === 1 ||
                            (step === 2 && !isOneAdult(_values))
                          ) {
                            navigateTo(step + 1)
                          } else {
                            submitApplication({
                              ..._values,
                              apartmentCode,
                              language: currentLanguage,
                              applicationId: applicationId
                            })
                          }
                        }}
                        back={() => {
                          navigateTo(step - 1)
                        }}
                        step={step}
                        initialValues={{ ...value }}
                      />
                    ))}
                </Grid>
              </>
            )}
        </>
      ) : (
        <LinearProgress />
      )}
    </>
  )
}

interface PropsFromState {
  loading: boolean
  application: Application | null
  error: string | null
  lead: PartnerLead | null
}

const mapStateToProps = (state: StateInterface): PropsFromState => {
  const createApplication = selectCreateApplication(state)
  const getLeadApplication = selectLeadApplication(state)
  return {
    ...createApplication,
    ...getLeadApplication
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createApplication: (data: any) => dispatch(createApplication(data)),
  getLeadData: (leadId: string) => dispatch(getApartmentLead(leadId))
})

export default connect(mapStateToProps, mapDispatchToProps)(App)
